/** The seven contracts. See scope.md §3.3. */

import type { MeldKind, NaturalRank, RoomRules, Suit } from '@garbage/shared';
import { MIN_RUN, ROUNDS } from '@garbage/shared';

export type Requirement = { kind: MeldKind; length: number };

/**
 * A round's contract.
 *
 * `useWholeHand` marks the final round: the runs must account for every card held, so
 * the player goes out by laying them and never makes a final discard. The lengths in
 * `requirements` are then minimums rather than exact sizes.
 */
export type Contract = { requirements: Requirement[]; useWholeHand: boolean };

const FIXED: Requirement[][] = [
  [{ kind: 'set', length: 3 }, { kind: 'set', length: 3 }],
  [{ kind: 'set', length: 3 }, { kind: 'run', length: 4 }],
  [{ kind: 'run', length: 4 }, { kind: 'run', length: 4 }],
  [{ kind: 'set', length: 3 }, { kind: 'set', length: 3 }, { kind: 'set', length: 3 }],
  [{ kind: 'set', length: 3 }, { kind: 'set', length: 3 }, { kind: 'run', length: 4 }],
  [{ kind: 'set', length: 3 }, { kind: 'run', length: 4 }, { kind: 'run', length: 4 }],
];

/** Cards dealt in a round: 6 + round, so 7 through 13. */
export function cardsDealt(round: number): number {
  return 6 + round;
}

export function contractFor(round: number, rules: RoomRules): Contract {
  if (round < 1 || round > ROUNDS) throw new Error(`No contract for round ${round}`);
  if (round < ROUNDS) return { requirements: FIXED[round - 1]!, useWholeHand: false };

  const run = (length: number): Requirement => ({ kind: 'run', length });
  switch (rules.finalContract) {
    case 'threeRunsOf4':
      return { requirements: [run(4), run(4), run(4)], useWholeHand: false };
    case 'twoRunsOf6':
      return { requirements: [run(6), run(6)], useWholeHand: false };
    case 'run13':
      return { requirements: [run(13)], useWholeHand: false };
    case 'threeRunsAllCards':
    default:
      // Three runs of at least four, between them using every card in hand.
      return { requirements: [run(MIN_RUN), run(MIN_RUN), run(MIN_RUN)], useWholeHand: true };
  }
}

/** Plain English, for the contract strip and the how-to-play page. */
export function describeContract(contract: Contract | readonly Requirement[]): string {
  const spec: Contract = Array.isArray(contract)
    ? { requirements: contract as Requirement[], useWholeHand: false }
    : (contract as Contract);
  const { requirements, useWholeHand } = spec;
  if (useWholeHand) {
    return `${requirements.length} runs of at least ${requirements[0]?.length ?? MIN_RUN}, using every card`;
  }
  return describeFixed(requirements);
}

function describeFixed(requirements: readonly Requirement[]): string {
  const counts = new Map<string, number>();
  for (const requirement of requirements) {
    const key = `${requirement.kind}:${requirement.length}`;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  const parts: string[] = [];
  for (const [key, count] of counts) {
    const [kind, length] = key.split(':');
    const noun = kind === 'set' ? 'set' : 'run';
    parts.push(`${count} ${noun}${count === 1 ? '' : 's'} of ${length}`);
  }
  return parts.join(' and ');
}

/**
 * Do these melds satisfy the contract?
 *
 * Under `exact` a set of three means three cards — a set of four is not a legal way to
 * lay it. Under `atLeast` a longer meld will do. Matching is greedy on sorted lengths,
 * which is correct because a longer meld can always cover a shorter requirement.
 */
export function matchesContract(
  melds: readonly { kind: MeldKind; length: number }[],
  contract: Contract,
  rules: RoomRules,
  handSize?: number,
): { ok: true } | { ok: false; reason: string } {
  const { requirements, useWholeHand } = contract;
  if (melds.length !== requirements.length) {
    return { ok: false, reason: `This round needs exactly ${requirements.length} melds` };
  }

  // The final round is laid out of the whole hand, with no card kept back to discard.
  if (useWholeHand) {
    for (const meld of melds) {
      if (meld.kind !== 'run') return { ok: false, reason: 'The last round is runs only' };
      if (meld.length < MIN_RUN) {
        return { ok: false, reason: `Every run needs at least ${MIN_RUN} cards` };
      }
    }
    const laid = melds.reduce((total, m) => total + m.length, 0);
    if (handSize !== undefined && laid !== handSize) {
      return {
        ok: false,
        reason: `The last round uses every card — lay all ${handSize}, with nothing left to throw`,
      };
    }
    return { ok: true };
  }

  // Match by kind first. Sorting on length alone is ambiguous when a set and a run
  // are the same size, which made the comparison depend on the order they arrived in.
  for (const kind of ['set', 'run'] as const) {
    const wanted = requirements.filter((r) => r.kind === kind).map((r) => r.length).sort((a, b) => a - b);
    const got = melds.filter((m) => m.kind === kind).map((m) => m.length).sort((a, b) => a - b);

    if (wanted.length !== got.length) {
      return { ok: false, reason: `This round needs ${describeContract(requirements)}` };
    }
    // Shortest against shortest: a longer meld can always cover a shorter requirement,
    // so greedy matching on sorted lengths is correct.
    for (let i = 0; i < wanted.length; i += 1) {
      const need = wanted[i]!;
      const have = got[i]!;
      const fits = rules.layDown === 'exact' ? have === need : have >= need;
      if (!fits) {
        return {
          ok: false,
          reason: rules.layDown === 'exact'
            ? `Lay exactly ${describeContract(requirements)}`
            : `This round needs ${describeContract(requirements)}`,
        };
      }
    }
  }
  return { ok: true };
}

/**
 * A worked example of this round's contract, in real cards.
 *
 * Shown at the start of every round, because "1 set of 3 and 1 run of 4" means very
 * little to somebody who has not played for a year, and nothing at all to a child.
 * Built from the contract itself, so it can never drift out of step with the rules.
 */
export function exampleFor(contract: Contract, handSize?: number): {
  kind: MeldKind; cards: { rank: NaturalRank; suit: Suit }[];
}[] {
  // Distinct ranks and suits per meld, so the example reads as several separate
  // things rather than one muddle.
  const setRanks: NaturalRank[] = ['7', 'Q', '3'];
  const runStarts: NaturalRank[] = ['4', '9', '2'];
  const runSuits: Suit[] = ['spades', 'hearts', 'diamonds'];
  const setSuits: Suit[] = ['spades', 'hearts', 'clubs', 'diamonds'];

  let lengths = contract.requirements.map((r) => r.length);
  if (contract.useWholeHand && handSize !== undefined) {
    // The final round is laid out of the whole hand, so show a split that adds up.
    const split = runLengthSplitsForExample(handSize, contract.requirements.length, lengths[0] ?? 4);
    if (split) lengths = split;
  }

  let setIndex = 0;
  let runIndex = 0;

  return contract.requirements.map((requirement, i) => {
    const length = lengths[i] ?? requirement.length;
    if (requirement.kind === 'set') {
      const rank = setRanks[setIndex % setRanks.length]!;
      setIndex += 1;
      return {
        kind: 'set' as const,
        cards: Array.from({ length }, (_, n) => ({ rank, suit: setSuits[n % setSuits.length]! })),
      };
    }
    const suit = runSuits[runIndex % runSuits.length]!;
    const start = ORDER.indexOf(runStarts[runIndex % runStarts.length]!);
    runIndex += 1;
    return {
      kind: 'run' as const,
      cards: Array.from({ length }, (_, n) => ({
        rank: ORDER[Math.min(start + n, ORDER.length - 1)]!,
        suit,
      })),
    };
  });
}

const ORDER: NaturalRank[] = ['A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K'];

function runLengthSplitsForExample(total: number, parts: number, minimum: number): number[] | null {
  const out: number[] = Array.from({ length: parts }, () => minimum);
  let left = total - minimum * parts;
  if (left < 0) return null;
  // Pile the remainder onto the last run, which shows the shape most clearly.
  out[parts - 1] = (out[parts - 1] ?? minimum) + left;
  left = 0;
  return out;
}
